import React from "react";
import ReactDOM from "react-dom/client";
import { RouteObject, RouterProvider, createBrowserRouter } from "react-router-dom";

import App from "./App.tsx";
import NotFound404 from "utils/NotFound404";
import Login from "pages/Login";
import { ThemeProvider } from "utils/theme";
import Dashboard from "pages/Dashboard/Dashboard.tsx";
import Users from "pages/Users/Users.tsx";
import BlogsAll from "pages/BlogsAll/BlogsAll.tsx";
import BlogItem from "pages/BlogItem";
import PageItem from "pages/PageItem/PageItem.tsx";

import "./styles/reset.css";
import "./styles/fonts.css";
import "./styles/index.css";
import PageSeo from "pages/PageSeo/PageSeo.tsx";
import Category from "pages/Category/Category.tsx";
import CategoryItem from "pages/CategoryItem/CategoryItem.tsx";
import Gallery from "pages/Gallery/Gallery.tsx";
import CustomerStory from "pages/CustomerStory/CustomerStory.tsx";
import CustomerStoryItem from "pages/CustomerStoryItem/CustomerStoryItem.tsx";
import TestimonialItem from "pages/TestimonialItem/TesimonialItem.tsx";
import Testimonial from "pages/TestimonialAll/Testimonial.tsx";
import LeadCases from "pages/LeadCases";
import Affiliates from "pages/Affiliates";
import ApplicationItem from "pages/ApplicationItem";
import ApplicationsAll from "pages/ApplicationsAll";
import ViewDocs from "pages/ViewDocs";
import AffiliateItem from "pages/AffiliateItem";
import ChatAndPreview from "pages/ChatAndPreview/ChatAndPreview.tsx";
import LoanIntelligence, { ExternalLoanIntelligence } from "pages/LoanIntelligence/LoanIntelligence.tsx";
import AssignmentAll from "pages/AssignmentAll";
import AffiliatePlans from "pages/AffiliatePlans/AffiliatePlans.tsx";
import DebtorSummary from "pages/DebtorSummary/NewDebtorSummary.tsx";
import CalculatorExecute, { ExternalCalculatorExecute } from "pages/LoanIntelligence/CalculatorExecute.tsx";
import { ClientError } from "utils/NotFound404/NotFound404.tsx";
import Credits from "pages/Credits/Credits.tsx";
import Notifications from "pages/Notifications/Notifications.tsx";
import OrganisationsAll from "pages/Organisation/Organisation.tsx";
import Journey from "pages/Journey/Journey.tsx";
import Negotiation from "pages/LoanIntelligence/Negotiation.tsx";
import Events from "pages/Events/Events.tsx";
import EventItem from "pages/EventItem/EventItem.tsx";
import BankAccount from "pages/BankAccount/BankAccount.tsx";
import EmailDashboard from "pages/EmailItem/EmailItem.tsx";
import CreditorsAll from "pages/Creditors/Creditors.tsx";
import { ONBOARDING_ROUTES } from "utils/routes.ts";
import OnboardingStatus from "pages/Onboarding/OnboardingStatus.tsx";
import PersonalInfo from "pages/Onboarding/PersonalInfo.tsx";
import { UploadLoanDocs } from "pages/Onboarding/UploadLoanDocs/UploadLoanDocs.tsx";
import { LoanDocsTable } from "pages/Onboarding/UploadLoanDocs/LoanDocsTable.tsx";
import { UploadLawsuits } from "pages/Onboarding/UploadLoanDocs/UploadLawsuits.tsx";
import { UploadBankDocs } from "pages/Onboarding/UploadBankDocs/UploadBankDocs.tsx";

import CreateApplication from "pages/CreateApplication/CreateApplication.tsx";
import EmailTemplate from "pages/EmailTemplate/EmailTemplate.tsx";
import KeyValueMapper from "pages/PandaDocMapper/KeyValueMapper.tsx";
import BoardSectionList from "components/Kanban/BoardSectionList.tsx";
import SelectStrategy from "pages/SelectStrategy";
import BackgroundJobLogs from "pages/BackgroundJobLogs/BackgroundJobLogs.tsx";
import TransactionLogs from "pages/TransactionLogs/TransactionLogs.tsx";
import PaymentCollect from "pages/PaymentCollect/PaymentCollect.tsx";
import SuccessPage from "pages/SuccessPage/SuccessPage.tsx";
import PipelineBoards from "components/Kanban/AllBoards.tsx";
import PaymentJobMonitor from "pages/PaymentJobs/PaymentJobMonitor.tsx";
import EmailSyncQueue from "pages/EmailSync/EmailSync.tsx";
import Communication from "pages/Communication/Communication.tsx";
import NotificationChannels from "pages/CommunicationChannel/CommunicationChannel.tsx";
import TaskTemplateListPage from "pages/TaskTemplate/TaskTemplateListPage.tsx";
import TaskTemplateDetailPage from "pages/TaskTemplate/TaskTemplateDetailPage.tsx";

const pipelinePages: RouteObject[] = [
  {
    path: "/dashboard/operations/pipelines",
    element: <PipelineBoards />,
  },
  {
    path: `/dashboard/operations/pipelines/:id`,
    element: <BoardSectionList />,
  },
];

const taskPages: RouteObject[] = [
  {
    path: "/dashboard/operations/tasks",
    element: <TaskTemplateListPage />,
  },
  {
    path: "/dashboard/operations/tasks/:id",
    element: <TaskTemplateDetailPage />,
  },
];

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Login />,
  },
  {
    path: "/file/:fileId",
    element: <ViewDocs />,
  },
  {
    path: "/dashboard/application/bank-statements/:appkey",
    element: <BankAccount />,
  },
  // {
  //   path: "/register",
  //   element: <Register />,
  // },
  // {
  //   path: "/forgot-password",
  //   element: <ForgotPassword />,
  // },
  // {
  //   path: "/reset-password/:token",
  //   element: <ResetPassword />,
  // },
  {
    path: "/dashboard/application/strategy/:appkey",
    element: <SelectStrategy />,
  },
  {
    path: "/dashboard",
    element: <Dashboard />,
  },
  {
    path: "/dashboard/lead-cases",
    element: <LeadCases />,
  },
  {
    path: "/dashboard/assignments",
    element: <AssignmentAll />,
  },
  {
    path: "/dashboard/applications",
    element: <ApplicationsAll />,
  },
  {
    path: "/dashboard/creditors",
    element: <CreditorsAll />,
  },
  {
    path: "/dashboard/application/key-mapping",
    element: <KeyValueMapper />,
  },
  {
    path: "/dashboard/application/:appKey",
    element: <ApplicationItem />,
  },
  {
    path: "/dashboard/application/background-logs/:appkey",
    element: <BackgroundJobLogs />,
  },
  {
    path: "/dashboard/application/:appKey/ai-audit/:reportId",
    element: <ChatAndPreview />,
  },
  {
    path: "/dashboard/application/:appKey/calculator",
    element: <LoanIntelligence />,
  },

  {
    path: "/dashboard/application/:appKey/calculator-execute",
    element: <CalculatorExecute />,
  },
  {
    path: "/dashboard/application/:appKey/notifications",
    element: <Notifications />,
  },
  {
    path: "/dashboard/application/:appKey/communication",
    element: <Communication />,
  },
  {
    path: "/dashboard/application/:appKey/negotiation/:loanId",
    element: <Negotiation />,
  },
  {
    path: "/external/:appKey/calculator",
    element: <ExternalLoanIntelligence />,
  },
  {
    path: "/external/:appKey/calculator-execute",
    element: <ExternalCalculatorExecute />,
  },
  {
    path: "/dashboard/blogs",
    element: <BlogsAll />,
  },
  {
    path: "/dashboard/users",
    element: <Users />,
  },
  {
    path: "/dashboard/affiliates",
    element: <Affiliates />,
  },
  {
    path: "/dashboard/affiliate/:id?",
    element: <AffiliateItem />,
  },
  {
    path: "/dashboard/affiliate-plans",
    element: <AffiliatePlans />,
  },
  {
    path: "/dashboard/journey",
    element: <Journey />,
  },
  {
    path: "/dashboard/create-blog",
    element: <BlogItem />,
  },
  {
    path: "/dashboard/blog/:blogSlug",
    element: <BlogItem />,
  },
  {
    path: "/dashboard/categories",
    element: <Category />,
  },
  {
    path: "/dashboard/create-category",
    element: <CategoryItem />,
  },
  {
    path: "/dashboard/category/:slug",
    element: <CategoryItem />,
  },
  {
    path: "/dashboard/gallery",
    element: <Gallery />,
  },
  {
    path: "/dashboard/page-seo",
    element: <PageSeo />,
  },
  ...pipelinePages,
  ...taskPages,
  {
    path: "/dashboard/page/build",
    element: <PageItem />,
  },
  {
    path: "/dashboard/customer-stories",
    element: <CustomerStory />,
  },
  {
    path: "/dashboard/testimonials",
    element: <Testimonial />,
  },
  // {
  //   path: "/dashboard/kanban",
  //   element: <Kanban />,
  // },
  {
    path: "/dashboard/communications",
    element: <NotificationChannels />, // TO BE UPDATED
  },
  /** @deprecated */
  {
    path: "/dashboard/organisation/communication",
    element: <Communication />,
  },
  {
    path: "/dashboard/communications/conversations",
    element: <Communication source="org" />,
  },
  {
    path: "/dashboard/communications/templates",
    element: <EmailTemplate />,
  },
  {
    path: "/dashboard/create-story",
    element: <CustomerStoryItem />,
  },
  {
    path: "/dashboard/story/:storySlug",
    element: <CustomerStoryItem />,
  },

  {
    path: "/dashboard/create-testimonial",
    element: <TestimonialItem />,
  },
  {
    path: "/dashboard/testimonial/:slug",
    element: <TestimonialItem />,
  },
  {
    path: "/dashboard/application/:app_key/debtor-summary",
    element: <DebtorSummary />,
  },
  {
    path: "/dashboard/application/:app_key/email",
    element: <EmailDashboard />,
  },
  {
    path: "/dashboard/emails/:orgCode",
    element: <EmailDashboard />,
  },
  {
    path: "/dashboard/sync-mails",
    element: <EmailSyncQueue />,
  },
  {
    path: "/dashboard/credits",
    element: <Credits />,
  },
  {
    path: "/dashboard/notifications",
    element: <Notifications />,
  },
  {
    path: "/dashboard/organisations",
    element: <OrganisationsAll />,
  },
  {
    path: "/dashboard/organisation/:orgcode/channel",
    element: <NotificationChannels />,
  },
  {
    path: "/dashboard/events",
    element: <Events />,
  },
  {
    path: "/dashboard/event/:id",
    element: <EventItem />,
  },
  {
    path: "/dashboard/create-application",
    element: <CreateApplication />,
  },
  //onboarding routes
  {
    path: ONBOARDING_ROUTES.Status,
    element: <OnboardingStatus />,
  },
  {
    path: ONBOARDING_ROUTES.PersonalInfo,
    element: <PersonalInfo />,
  },
  {
    path: ONBOARDING_ROUTES.UploadLoanDocs_1,
    element: <UploadLoanDocs />,
  },
  {
    path: ONBOARDING_ROUTES.UploadLoanDocs_2,
    element: <LoanDocsTable />,
  },
  {
    path: ONBOARDING_ROUTES.UploadLoanDocs_3, // lawsuit
    element: <UploadLawsuits />,
  },
  {
    path: ONBOARDING_ROUTES.UploadBankDocs_1,
    element: <UploadBankDocs />,
  },

  {
    path: "/dashboard/transaction-logs",
    element: <TransactionLogs />,
  },
  {
    path: "/collect/payment-info",
    element: <PaymentCollect />,
  },
  {
    path: "/payment-method-success",
    element: <SuccessPage />,
  },
  {
    path: "/ledger/payment-monitor",
    element: <PaymentJobMonitor />,
  },

  {
    path: "*",
    element: <NotFound404 />,
  },
].map((item) => ({
  ...item,
  errorElement: <ClientError />,
}));

const router = createBrowserRouter(routes);

if (import.meta.hot) {
  import.meta.hot.dispose(() => router.dispose());
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <App>
        <RouterProvider router={router} />
      </App>
    </ThemeProvider>
  </React.StrictMode>,
);
